import httpPortalJob from "@/confs/interceptors/http_portal_jobs";
import type { CustomAxiosRequestConfig } from "@/confs/interceptors/utils/interface";
import type { Candidate } from "@/interfaces/candidates";
import type { PaginationFilter, PaginationResult } from "@/interfaces/common/paginations";
import type { Database } from "@/interfaces/databases";
import type { FileData } from "@/interfaces/general";
import type { AtsForm, PortalFormItem, PublicCandidateFormAnswer } from "@/interfaces/ats";
import type { Process, ProcessCandidate } from "@/interfaces/processes";
import axios from "axios";
import { httpService } from "./utils/http_service";

export const portalJobsService = {
    async existsCandidateMail(vc_email: string): Promise<{ exists: boolean }> {
        const response = await httpPortalJob.get(`/portal-jobs/public/candidates/exists-mail`,
            { params: { vc_email } });
        return response.data;
    },
    async createCandidate(data: Candidate | {}): Promise<{ candidate: Candidate, token: string }> {
        const response = await httpPortalJob.post(`/portal-jobs/public/candidates/`, data);
        return response.data;
    },
    async sendConfirmCandidateMail(): Promise<{ exists: boolean }> {
        const response = await httpPortalJob.post(`/portal-jobs/candidates/confirm-mail/`, {});
        return response.data;
    },
    async confirmCandidateMail(hash: string): Promise<string> {
        const response = await httpPortalJob.post(`/portal-jobs/public/candidates/confirm-mail/${hash}`, {});
        return response.data;
    },
    async login(vc_usuario: string, vc_senha: string): Promise<string> {
        const response = await httpPortalJob.post(`/portal-jobs/public/login`, { vc_usuario, vc_senha })
        return response.data;
    },
    async refreshToken(): Promise<string> {
        const res = await axios.get(`${import.meta.env.VITE_APP_URL}/portal-jobs/public/refresh-token`, {
            withCredentials: true // envia o cookie
        });
        return res.data.data
    },
    async candidateInfo(): Promise<Candidate> {
        const response = await httpPortalJob.get(`/portal-jobs/candidates/info`);
        return response.data;
    },
    async logout(): Promise<Candidate> {
        const response = await httpPortalJob.get(`/portal-jobs/public/logout`);
        return response.data;
    },
    async recoverSendCode(vc_email: string): Promise<{ vc_hash: string }> {
        const response = await httpPortalJob.post(`/portal-jobs/public/recover/send-code`, { vc_email });
        return response.data;
    },
    async recoverVerifyCode(vc_hash: string, vc_codigo: string) {
        const response = await httpPortalJob.post(`/portal-jobs/public/recover/verify-code`, { vc_hash, vc_codigo });
        return response.data;
    },
    async recover(vc_hash: string, vc_codigo: string, vc_senha: string): Promise<string> {
        const response = await httpPortalJob.post(`/portal-jobs/public/recover`, { vc_hash, vc_codigo, vc_senha });
        return response.data;
    },
    async getDatabaseCandidates(): Promise<Database> {
        const response = await httpPortalJob.get(`/portal-jobs/databases/candidates`);
        return response.data;
    },
    async getDatabaseClients(): Promise<Database> {
        const response = await httpPortalJob.get(`/portal-jobs/databases/clients`);
        return response.data;
    },
    async uploadResume(file: FileData, skipDataUpdate: boolean = false): Promise<void> {
        const response = await httpPortalJob.post(`/portal-jobs/candidates/resume/upload`,
            { ...file, bl_ignorar_atualizacao_dados: skipDataUpdate });
        return response.data;
    },
    async downloadResume() {
        const res = await httpPortalJob.post(`/portal-jobs/candidates/resume/export`, {}, {
            responseType: 'blob',
            fullResponse: true
        } as CustomAxiosRequestConfig);

        // Cria um link temporário para download
        const url = window.URL.createObjectURL(res.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', "curriculo.pdf");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },
    async downloadOriginalResume(file: FileData) {
        const res = await httpPortalJob.get(
            `/portal-jobs/candidates/view-file?id_campo=ar_anexo_curriculo&vc_nome_arquivo=${encodeURIComponent(file.vc_nome)}`,
            {
                responseType: 'blob',
                fullResponse: true
            } as CustomAxiosRequestConfig
        );

        // Cria um link temporário para download
        const url = window.URL.createObjectURL(res.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.vc_nome);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },


    async getAllProcesses(filter: PaginationFilter): Promise<PaginationResult<Process>> {
        const response = await httpPortalJob.get(
            `/portal-jobs/public/processes?${httpService.buildQueryParams(filter)}`, {
                skipLoading: true
            } as CustomAxiosRequestConfig);
        return response.data;
    },
    async getProcessById(id: string): Promise<Process> {
        const response = await httpPortalJob.get(`/portal-jobs/public/processes/${id}`, {
            skipLoading: true
        } as CustomAxiosRequestConfig);
        return response.data;
    },
    async getProcessByCode(code: string): Promise<Process> {
        const response = await httpPortalJob.get(`/portal-jobs/public/processes/code/${code}`, {
            skipLoading: true
        } as CustomAxiosRequestConfig);
        return response.data;
    },
    async getFormById(id: string): Promise<AtsForm> {
        const response = await httpPortalJob.get(`/portal-jobs/public/forms/${id}`);
        return response.data;
    },
    async applyToProcess(id: string, formAnswers?: Record<string, unknown>, candidateFormAnswers?: Record<string, unknown>): Promise<void> {
        const body: Record<string, unknown> = {};
        if (formAnswers) body.js_respostas_formulario = formAnswers;
        if (candidateFormAnswers) body.js_respostas_formulario_candidato = candidateFormAnswers;
        const response = await httpPortalJob.post(`/portal-jobs/processes/${id}/apply`, body);
        return response.data;
    },
    async acceptInvite(id: string): Promise<void> {
        const response = await httpPortalJob.post(`/portal-jobs/processes/${id}/invite/accept`);
        return response.data;
    },
    async rejectInvite(id: string): Promise<void> {
        const response = await httpPortalJob.post(`/portal-jobs/processes/${id}/invite/reject`);
        return response.data;
    },
    async getMyProcesses(): Promise<ProcessCandidate[]> {
        const response = await httpPortalJob.get(`/portal-jobs/processes/my`);
        return response.data;
    },
    async getValuationQuestions(name: string): Promise<any> {
        const response = await httpPortalJob.get(`/portal-jobs/evaluations/questions/${name}`);
        return response.data;
    },
    async finishValuation(name: string): Promise<any> {
        const response = await httpPortalJob.post(`/portal-jobs/evaluations/${name}/finish`);
        return response.data;
    },
    async getMyForms(): Promise<PortalFormItem[]> {
        const response = await httpPortalJob.get(`/portal-jobs/forms/my`);
        return response.data;
    },
    async submitFormAnswers(processId: string, formId: string, answers: Record<string, unknown>): Promise<void> {
        const response = await httpPortalJob.post(`/portal-jobs/processes/${processId}/forms/${formId}/answer`, { answers });
        return response.data;
    },
    async submitCandidateFormAnswers(formId: string, answers: Record<string, unknown>): Promise<void> {
        const response = await httpPortalJob.post(`/portal-jobs/forms/candidate/${formId}/answer`, { answers });
        return response.data;
    },
    async submitLinkedCandidateFormAnswers(formId: string, answers: Record<string, unknown>, processId?: string): Promise<void> {
        const response = await httpPortalJob.post(`/portal-jobs/forms/candidate/${formId}/submit`,
            { answers, id_processo: processId });
        return response.data;
    },
    async submitPublicCandidateFormAnswers(formId: string, data: PublicCandidateFormAnswer): Promise<void> {
        const response = await httpPortalJob.post(`/portal-jobs/public/forms/candidate/${formId}/public-answer`, data);
        return response.data;
    }
}
